import { useState } from "react";
import { Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { formatRelative } from "@/lib/utils";
import { useJourneyTaskComments } from "./use-journey-task-comments";
import { useAddTaskComment } from "./use-add-task-comment";

// Inline thread on a journey task. Mirrors the MDF thread pattern: query
// only mounts when expanded, optimistic append on add, two visual variants
// for partner vs internal authors.

type Props = {
  progressId: string;
  expanded: boolean;
};

export function TaskCommentThread({ progressId, expanded }: Props) {
  const { data, isLoading, isError } = useJourneyTaskComments(progressId, {
    enabled: expanded,
  });
  const add = useAddTaskComment();
  const [body, setBody] = useState("");

  if (!expanded) return null;

  function handleSend() {
    const trimmed = body.trim();
    if (!trimmed) return;
    add.mutate(
      { progressId, body: trimmed },
      { onSuccess: () => setBody("") },
    );
  }

  return (
    <section className="border-t pt-4">
      <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Comments
      </h4>

      {isLoading ? (
        <div className="mt-3 space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-2/3" />
        </div>
      ) : isError ? (
        <p className="mt-3 text-xs text-muted-foreground">
          Couldn’t load comments. Refresh to try again.
        </p>
      ) : (data ?? []).length === 0 ? (
        <p className="mt-3 text-xs text-muted-foreground">
          No comments yet.
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {(data ?? []).map((c) => {
            const isPartner = c.author_role === "partner";
            return (
              <li
                key={c.id}
                className={
                  isPartner
                    ? "rounded-md border bg-card p-2.5"
                    : "rounded-md border border-accent/30 bg-accent/5 p-2.5"
                }
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-foreground">
                    {isPartner ? "You" : "Bookboost team"}
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    {formatRelative(c.created_at)}
                  </span>
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">
                  {c.body}
                </p>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-3">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Add context or ask a question…"
          rows={2}
          disabled={add.isPending}
          maxLength={4000}
        />
        <div className="mt-2 flex justify-end">
          <Button
            type="button"
            size="sm"
            onClick={handleSend}
            disabled={add.isPending || body.trim().length === 0}
          >
            {add.isPending ? (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Send className="mr-2 h-3.5 w-3.5" />
            )}
            Send
          </Button>
        </div>
      </div>
    </section>
  );
}
